import { Link } from "react-router-dom";
import { Icon } from "@/lib/icons";
import { contributorTasks } from "@/features/contributor-tasks/contributor-tasks";
import type { PublicHomeCopy } from "./public-home-copy";
import { HomeSection, SectionHeading } from "./HomeParts";

export function HowItWorks({ copy }: { copy: PublicHomeCopy }) {
  const c = copy.how;
  return (
    <HomeSection labelledBy="home-how-title">
      <SectionHeading id="home-how-title" eyebrow={c.eyebrow} title={c.title} />
      <ol className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6">
        {c.steps.map(([title, body], index) => (
          <li key={title} className="border-t border-border pt-6">
            <span className="text-sm font-semibold tabular-nums text-brand">{String(index + 1).padStart(2, "0")}</span>
            <h3 className="mt-3 text-xl font-semibold tracking-[-0.02em] text-foreground">{title}</h3>
            <p className="mt-2 max-w-sm text-[15px] leading-7 text-muted-foreground">{body}</p>
          </li>
        ))}
      </ol>
    </HomeSection>
  );
}

/** The four tasks of the mobile app's home grid, in its words. */
const WAY_KEYS = ["speak", "transcribe", "translate", "verifyAudio"] as const;

interface ContributeWaysProps {
  copy: PublicHomeCopy;
  lang: string;
  /**
   * Where a card leads: the task itself for a signed-in contributor, the
   * reader's own space for other signed-in roles, sign-up for a visitor.
   */
  target: "task" | "signup" | { href: string };
}

export function ContributeWays({ copy, lang, target }: ContributeWaysProps) {
  const c = copy.ways;
  const tasks = contributorTasks(lang).filter((task) => (WAY_KEYS as readonly string[]).includes(task.key));
  const ordered = WAY_KEYS.map((key) => tasks.find((task) => task.key === key)).filter((task) => task !== undefined);
  const hrefOf = (taskHref: string) =>
    target === "task" ? taskHref : target === "signup" ? `/${lang}/signup` : target.href;

  return (
    <HomeSection labelledBy="home-ways-title">
      <SectionHeading id="home-ways-title" eyebrow={c.eyebrow} title={c.title} />
      <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {ordered.map((task) => (
          <li key={task.key}>
            <Link
              to={hrefOf(task.href)}
              className="group flex h-full flex-col rounded-3xl border border-border bg-surface p-6 transition-colors hover:border-primary/50 focus-ring"
            >
              <span className="grid h-11 w-11 place-items-center rounded-2xl border border-border bg-background text-brand">
                <Icon name={task.icon} className="h-5 w-5" />
              </span>
              <span className="mt-6 text-lg font-semibold tracking-[-0.02em] text-foreground">{task.label}</span>
              <span className="mt-1.5 text-sm leading-6 text-muted-foreground">{task.hint}</span>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                {c.cta}
                <Icon name="arrowRight" className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </HomeSection>
  );
}
